import React, { useState, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useOrganization } from "../context/OrganizationContext";
import TrialBanner from "./TrialBanner";
import UpgradeModal from "./UpgradeModal";
import axios from "axios";
import { setAuthToken } from "../lib/axiosAuth";
import {
  LayoutDashboard, Users, ArrowLeftRight, FileText,
  Shield, LogOut, Building2, ChevronLeft, ChevronRight,
  Lock, FileDown, UserCog, Search, FileCode, Settings,
  Crown, Sparkles, User, ArrowLeft, Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Navigation items with feature/permission requirements
const NAV_ITEMS = [
  { 
    to: "/dashboard", 
    icon: LayoutDashboard, 
    label: "Dashboard",
    permission: "view_dashboard",
  },
  { 
    to: "/customers", 
    icon: Users, 
    label: "Clientes (KYC)",
    permission: "view_customers",
  },
  { 
    to: "/operations", 
    icon: ArrowLeftRight, 
    label: "Operaciones",
    permission: "view_operations",
  },
  { 
    to: "/screening", 
    icon: Search, 
    label: "Screening PEP",
    permission: "run_screening",
  },
  { 
    to: "/aml-reports", 
    icon: FileCode, 
    label: "Avisos AML",
    feature: "reportes_xml_sat_uif",
    permission: "generate_xml_reports",
    requiredPlan: "standard",
  },
  { 
    to: "/privacy", 
    icon: Lock, 
    label: "Privacidad & ARCO",
    permission: "manage_privacy",
  },
  { 
    to: "/reports", 
    icon: FileDown, 
    label: "Reportes PDF",
    permission: "generate_reports",
  },
  { 
    to: "/users", 
    icon: UserCog, 
    label: "Usuarios",
    permission: "manage_users",
  },
  { 
    to: "/audit-logs", 
    icon: FileText, 
    label: "Bitácora",
    feature: "bitacora_audit_log",
    permission: "view_audit_logs",
    requiredPlan: "pro",
  },
  { 
    to: "/organization-settings", 
    icon: Settings, 
    label: "Configuración",
    permission: "edit_org_settings",
  },
];

// Plan badge colors
const PLAN_COLORS = {
  free: "bg-slate-600 text-slate-200",
  basic: "bg-blue-600 text-blue-100",
  standard: "bg-purple-600 text-purple-100",
  pro: "bg-amber-600 text-amber-100",
  enterprise: "bg-emerald-600 text-emerald-100",
};

export default function Layout({ children }) {
  const { user, logout, checkAuth } = useAuth();
  const { organization, planName, plan, hasFeature, hasPermission, loading: orgLoading } = useOrganization();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [endingImpersonation, setEndingImpersonation] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleReturnToAdmin = () => {
    try { sessionStorage.removeItem("notalia_admin_viewing_app"); } catch { /* private mode */ }
    navigate("/superadmin");
  };

  const handleEndImpersonation = async () => {
    setEndingImpersonation(true);
    try {
      const res = await axios.post(`${API}/superadmin/end-impersonation`, {}, { withCredentials: true });
      const originalToken = res.data.original_token;
      if (originalToken) {
        setAuthToken(originalToken);
      }
      await checkAuth();
      try { sessionStorage.removeItem("notalia_admin_viewing_app"); } catch { /* private mode */ }
      navigate("/superadmin");
    } catch (err) {
      console.error("Error ending impersonation:", err);
    } finally {
      setEndingImpersonation(false);
    }
  };

  // Filter nav items based on plan and role
  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.map(item => {
      // Check permission first
      if (item.permission && !hasPermission(item.permission)) {
        return { ...item, hidden: true };
      }
      
      // Check feature availability
      if (item.feature) {
        const available = hasFeature(item.feature);
        return { ...item, locked: !available };
      }
      
      return item;
    }).filter(item => !item.hidden);
  }, [hasFeature, hasPermission]);

  return (
    <div className="flex h-screen bg-slate-950" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? "w-16" : "w-64"} bg-slate-900 border-r border-slate-800 flex flex-col transition-all duration-200`}
        data-testid="sidebar"
      >
        {/* Organization Header */}
        <div className={`border-b border-slate-800 ${collapsed ? "p-2" : "p-4"}`}>
          {/* Notalia Logo */}
          <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"} mb-3`}>
            {!collapsed && (
              <img src="/notalia-logo.png" alt="Notalia" className="h-6 brightness-0 invert opacity-60" />
            )}
            {collapsed && (
              <img src="/notalia-icon.png" alt="N" className="h-6 brightness-0 invert opacity-60" />
            )}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="text-slate-500 hover:text-slate-300 hidden lg:block"
              data-testid="sidebar-toggle"
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
          
          {/* Organization Info */}
          {!collapsed && organization && (
            <div className="mt-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                {organization.logo_url ? (
                  <img 
                    src={organization.logo_url} 
                    alt={organization.name} 
                    className="w-10 h-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {organization.name}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[plan] || PLAN_COLORS.free}`}>
                      {planName}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {collapsed && organization && (
            <div className="mt-2 flex justify-center">
              {organization.logo_url ? (
                <img 
                  src={organization.logo_url} 
                  alt={organization.name} 
                  className="w-8 h-8 rounded-lg object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto" data-testid="sidebar-nav">
          {visibleNavItems.map(({ to, icon: Icon, label, locked, requiredPlan }) => (
            <NavLink
              key={to}
              to={locked ? "#" : to}
              onClick={(e) => {
                if (locked) {
                  e.preventDefault();
                  // Could show upsell modal here
                }
              }}
              data-testid={`nav-${to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative group ${
                  locked
                    ? "text-slate-600 cursor-not-allowed"
                    : isActive
                      ? "bg-teal-600/10 text-teal-400 border border-teal-600/20"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                } ${collapsed ? "justify-center" : ""}`
              }
            >
              <Icon size={18} className={locked ? "opacity-50" : ""} />
              {!collapsed && (
                <>
                  <span className={locked ? "opacity-50" : ""}>{label}</span>
                  {locked && (
                    <div className="ml-auto flex items-center gap-1">
                      <Lock size={12} className="text-amber-500" />
                      <span className="text-xs text-amber-500 font-normal">
                        {requiredPlan === "standard" ? "Std+" : requiredPlan === "pro" ? "Pro+" : "Ent"}
                      </span>
                    </div>
                  )}
                </>
              )}
              
              {/* Tooltip for locked items when collapsed */}
              {collapsed && locked && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                  <Lock size={10} className="inline text-amber-500 mr-1" />
                  Plan {requiredPlan}+
                </div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-slate-800">
          {!collapsed && user && (
            <div className="mb-3 px-2">
              <p className="text-sm font-medium text-slate-200 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
              <p className="text-xs text-slate-600 mt-0.5 capitalize">{user.role?.replace("_", " ")}</p>
            </div>
          )}
          
          {/* Mi Perfil Link - Available to all users */}
          <NavLink
            to="/perfil"
            className={({ isActive }) =>
              `flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm transition-colors mb-2 ${
                isActive
                  ? "bg-teal-600/10 text-teal-400 border border-teal-600/20"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              } ${collapsed ? "justify-center" : ""}`
            }
          >
            <User size={18} />
            {!collapsed && <span>Mi Perfil</span>}
          </NavLink>
          
          <button
            onClick={handleLogout}
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors ${collapsed ? "justify-center" : ""}`}
            data-testid="logout-button"
          >
            <LogOut size={18} />
            {!collapsed && <span>Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto flex flex-col">
        <TrialBanner onOpenPlans={() => setShowUpgradeModal(true)} />

        {/* Superadmin browsing banner */}
        {user?.role === "superadmin" && !user?.is_impersonation && (
          <div className="flex items-center justify-between px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-sm">
            <div className="flex items-center gap-2 text-amber-400">
              <Eye size={14} />
              <span>Estás viendo la app como superadmin</span>
            </div>
            <button
              onClick={handleReturnToAdmin}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors text-xs font-medium"
            >
              <ArrowLeft size={12} />
              Volver al panel admin
            </button>
          </div>
        )}

        {/* Impersonation banner */}
        {user?.is_impersonation && (
          <div className="flex items-center justify-between px-4 py-2 bg-purple-500/10 border-b border-purple-500/20 text-sm">
            <div className="flex items-center gap-2 text-purple-400">
              <Shield size={14} />
              <span>Impersonando a <strong>{user.name}</strong> ({user.email})</span>
            </div>
            <button
              onClick={handleEndImpersonation}
              disabled={endingImpersonation}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors text-xs font-medium disabled:opacity-50"
            >
              <ArrowLeft size={12} />
              {endingImpersonation ? "Saliendo..." : "Volver al superadmin"}
            </button>
          </div>
        )}

        <div className="p-6 lg:p-8 max-w-7xl mx-auto flex-1 w-full">
          {children}
        </div>
      </main>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentPlan={plan}
      />
    </div>
  );
}
